import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Compass, Home, MessageCircle, Search } from "lucide-react";
import { useSocialPaths } from "@/lib/socialPaths";
import { cn } from "@/lib/utils";

/** True when a Social DM thread (not the inbox list) is open. */
export function isSocialMobileThreadPath(pathname) {
  const p = String(pathname || "");
  return (
    /^\/messages\/[^/]+\/?$/.test(p) ||
    /^\/orgs\/[^/]+\/social\/messages\/[^/]+\/?$/.test(p) ||
    /^\/p\/[^/]+\/?$/.test(p) ||
    /^\/orgs\/[^/]+\/social\/p\/[^/]+\/?$/.test(p)
  );
}

const NAV_H = "3.5rem";

/** Padding so page content clears the fixed mobile bottom nav + home indicator. */
export const SOCIAL_MOBILE_NAV_PAD = `calc(${NAV_H} + env(safe-area-inset-bottom, 0px))`;

export default function SocialBottomNav({ className }) {
  const paths = useSocialPaths();
  const location = useLocation();

  const messagesTo = paths.messages();
  const homeTo = paths.home();
  const searchTo = paths.search();

  const items = [
    { id: "home", label: "Home", to: homeTo, icon: Home, end: true },
    { id: "explore", label: "Explore", to: paths.explore(), icon: Compass },
    {
      id: "messages",
      label: "Messages",
      to: messagesTo,
      icon: MessageCircle,
    },
    {
      id: "search",
      label: "Search",
      to: searchTo,
      icon: Search,
    },
  ];

  return (
    <nav
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm md:hidden",
        className
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Circa Social"
    >
      <ul className="grid h-14 grid-cols-4">
        {items.map((item) => {
          const Icon = item.icon;
          const searchActive =
            item.id === "search" &&
            (location.pathname === searchTo ||
              location.pathname.startsWith(`${searchTo}/`) ||
              /\/social\/search(?:\/|$)/.test(location.pathname) ||
              location.pathname === "/search");
          return (
            <li key={item.id} className="min-w-0">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex h-full min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium",
                    isActive || searchActive
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
